import { EnvironmentType } from "@app-context/environments/context";
import { DEFAULT_ENV } from "@app-context/environments/EnvironmentsProvider";
import { APISource, APISources } from "@app-context/networkCalls/types";
import {
  systematicAuthenticate,
  systematicCheckTwoFactorToken,
  systematicGenerateSecureAPIToken,
  systematicGenerateTwoFactorToken,
} from "@app-utils/systematicAuthentication/systematicAuth";
import { useRefCallback } from "@enfusion-ui/hooks";
import { AuthenticationResult } from "@enfusion-ui/types";
import { errorToast, successToast, useAuth } from "@enfusion-ui/web-core";
import React from "react";

import {
  DEFAULT_CONFIG,
  InspectorContext,
  InspectorContextConfig,
  InspectorContextValues,
  RequestType,
  RequestTypes,
} from "./context";

const defaultContext: InspectorContextValues = {
  requestType: RequestTypes.ENFUSION,
  apiSource: APISources.CORE,
  environment: { ...DEFAULT_ENV },
  config: { ...DEFAULT_CONFIG.rest },
  requiresTwoFactorAuthentication: false,
};

export const InspectorContextProvider: React.FC<{
  config: Partial<InspectorContextValues>;
}> = ({ config, children }) => {
  const { user } = useAuth();
  const [context, setContext] = React.useState<InspectorContextValues>(
    config
      ? {
          requestType: config.requestType ?? defaultContext.requestType,
          apiSource: config.apiSource ?? defaultContext.apiSource,
          environment: config.environment ?? { ...defaultContext.environment },
          config: config.config ?? { ...defaultContext.config },
          requiresTwoFactorAuthentication:
            config.requiresTwoFactorAuthentication ?? false,
        }
      : { ...defaultContext }
  );
  const {
    apiSource,
    environment,
    config: { username, password, adminpassword, adminuser, inputToken },
  } = context;

  const setConfig = useRefCallback(
    (config: InspectorContextConfig) => {
      setContext((p) => ({ ...p, config }));
    },
    [setContext]
  );

  const handleTypeChange = useRefCallback(
    (value: RequestType | null) => {
      if (value !== null) {
        setContext((p) => ({
          ...p,
          requestType: value,
          config: DEFAULT_CONFIG[value],
        }));
      }
    },
    [setContext]
  );

  const handleEnvironmentChange = useRefCallback(
    (value: EnvironmentType | null) => {
      if (value !== null) setContext((p) => ({ ...p, environment: value }));
    },
    [setContext]
  );

  const handleAPISourceChange = useRefCallback(
    (value: APISource | null) => {
      if (value !== null) setContext((p) => ({ ...p, apiSource: value }));
    },
    [setContext]
  );
  const authenticateWith2FA = useRefCallback(async () => {
    try {
      const secureTokenStorageKey = [
        "secureToken",
        apiSource,
        username,
        adminuser,
      ]
        .filter(Boolean)
        .join("///");

      const cryptoToken = await systematicGenerateTwoFactorToken(
        username,
        password,
        environment.restAPIUrl
      );
      const token = await systematicCheckTwoFactorToken(
        username,
        password,
        inputToken,
        cryptoToken,
        environment.restAPIUrl
      );
      successToast("2 Factor authentication successful");
      sessionStorage.setItem(
        secureTokenStorageKey,
        Object.entries(token)[0][0]
      );
    } catch (err) {
      if (!inputToken) errorToast("Token cannot be empty.");
      else errorToast("Error occurred.", (err as Error).message);
    }
  }, [username, password, adminuser, inputToken, environment.restAPIUrl]);

  const getSecureToken = useRefCallback(async () => {
    if (apiSource === APISources.INTERNAL) {
      if (!user)
        throw new Error("Please login to perform internal api requests");
      return user.authToken || "";
    }

    if (apiSource === APISources.CORE) {
      if (!username || !password)
        throw new Error("Username and password are required");

      let authenticatedUser: AuthenticationResult | null = null;

      try {
        authenticatedUser = await systematicAuthenticate(
          username,
          password,
          environment.restAPIUrl
        );
      } catch (err) {
        throw new Error("Authentication failed");
      }
      if (!authenticatedUser || !authenticatedUser.authenticated)
        throw new Error("Authentication failed");

      if (authenticatedUser.requiresTwoFactorAuthentication) {
        setContext((v) => ({
          ...v,
          requiresTwoFactorAuthentication: true,
        }));
        throw new Error("2 Factor Authentication required");
      }

      if (authenticatedUser.admin && (!adminuser || !adminpassword))
        throw new Error("Admin user and admin password are required");

      try {
        const secureTokenRes = await systematicGenerateSecureAPIToken(
          username,
          password,
          {
            adminuser,
            adminpassword,
          },
          environment.restAPIUrl
        );

        return secureTokenRes;
      } catch (err) {
        throw new Error("Authentication failed");
      }
    }

    return "";
  }, [context.config, setConfig, apiSource, environment.restAPIUrl, user]);

  const getOEMSSessionToken = useRefCallback(async () => {
    return getSecureToken();
  }, [apiSource, username]);

  return (
    <InspectorContext.Provider
      value={{
        ...context,
        setConfig,
        authenticateWith2FA,
        handleTypeChange,
        getSecureToken,
        getOEMSSessionToken,
        handleAPISourceChange,
        handleEnvironmentChange,
      }}
    >
      {children}
    </InspectorContext.Provider>
  );
};
